import {
  lazyPipe,
  validateApiRequest,
  validateApiResponse,
  withApiErrorCapture,
} from '@musinsa/apis-common-api-zod4';
import { executeStandardRequest } from '@musinsa/packages-http';
import urlJoin from 'url-join';
import z from 'zod';
import { privateHttpClient } from '../httpClient';
import { createApiResponseSchema } from '../utils';
import { curationsApiPath } from './apiPath';

/**
 * 큐레이션 내 개별 상품 아이템 스키마 (OpenAPI `GoodsItem` + `totalQuantity` 판매 수량)
 */
export const CurationGoodsItemSchema = z
  .object({
    goodsNo: z.number().describe('상품 번호'),
    imageUrl: z.string().describe('이미지 URL'),
    /** 스펙상 required이나 일부 플랫폼/응답에서 생략될 수 있음 */
    isLiked: z.boolean().nullish().describe('좋아요 여부'),
    brandNo: z.number().nullish().describe('브랜드 번호 (29CM 전용)'),
    brandName: z.string().describe('브랜드명'),
    goodsName: z.string().describe('상품명'),
    salesRate: z.number().int().nullish().describe('할인율'),
    price: z.number().int().describe('판매가'),
    normalPrice: z.number().int().nullish().describe('정상가 (MUSINSA 전용)'),
    couponPrice: z.number().int().nullish().describe('쿠폰 적용가 (MUSINSA 전용)'),
    couponSalesRate: z.number().int().nullish().describe('쿠폰 할인율 (MUSINSA 전용)'),
    extraDiscountAmount: z
      .number()
      .int()
      .nullish()
      .describe('추가할인 금액(원). 표시는 finalPrice 사용 (주문 도메인 전용, MUSINSA 전용)'),
    finalPrice: z
      .number()
      .int()
      .nullish()
      .describe('최종가 = 세일가 + 상품쿠폰 + 추가할인 (장바구니쿠폰 제외, MUSINSA 전용)'),
    finalDiscount: z
      .number()
      .int()
      .nullish()
      .describe('정상가 대비 finalPrice 할인율 (MUSINSA 전용)'),
    isLowestPrice: z
      .boolean()
      .nullish()
      .describe('RADAR 추가할인 최저가 보장 여부 — true 시 최저가도전 라벨 (MUSINSA 전용)'),
    isExclusive: z.boolean().nullish().describe('단독 상품 여부 (MUSINSA 전용)'),
    hasOptionPrice: z.boolean().nullish().describe('옵션가 존재 여부 (MUSINSA 전용)'),
    likeCount: z.number().int().nullish().describe('좋아요 수'),
    reviewScore: z.string().nullish().describe('리뷰 점수'),
    reviewCount: z.number().int().nullish().describe('리뷰 수'),
    existsCoupon: z.boolean().nullish().describe('쿠폰 존재 여부'),
    linkUrl: z.string().describe('상품 링크 URL'),
    isSoldOut: z.boolean().describe('품절 여부'),
    largeCategoryNo: z.number().nullish().describe('대분류 카테고리 번호 (29CM 전용)'),
    largeCategoryName: z.string().nullish().describe('대분류 카테고리명 (29CM 전용)'),
    middleCategoryNo: z.number().nullish().describe('중분류 카테고리 번호 (29CM 전용)'),
    middleCategoryName: z.string().nullish().describe('중분류 카테고리명 (29CM 전용)'),
    smallCategoryNo: z.number().nullish().describe('소분류 카테고리 번호 (29CM 전용)'),
    smallCategoryName: z.string().nullish().describe('소분류 카테고리명 (29CM 전용)'),
    totalQuantity: z.number().int().min(0).nullish().describe('판매 수량'),
  })
  .describe('CurationGoodsItemSchema');

/**
 * 큐레이션 내 개별 스냅 아이템 스키마 (OpenAPI `SnapItem`, MUSINSA SNAP2 전용)
 */
export const CurationSnapItemSchema = z
  .object({
    id: z.string().describe('스냅 ID'),
    imageUrl: z.string().describe('스냅 이미지 URL'),
    linkUrl: z.string().describe('스냅 링크 URL'),
  })
  .describe('CurationSnapItemSchema');

/**
 * 개별 큐레이션 정보 스키마 (OpenAPI `GetCurationsForMeV2Response`)
 */
export const CurationSchema = z
  .object({
    curationId: z.string().describe('큐레이션 ID'),
    title: z.string().describe('큐레이션 제목'),
    description: z.string().nullish().describe('큐레이션 설명'),
    type: z.string().nullish().describe('큐레이션 타입 (MUSINSA: GOODS/SNAP2, 29CM: null)'),
    ordNo: z.number().int().describe('순서 번호'),
    isPublished: z.boolean().nullish().describe('발행 여부 (MUSINSA 전용, 29CM: null)'),
    goodsItems: z.array(CurationGoodsItemSchema).describe('상품 목록'),
    snapItems: z.array(CurationSnapItemSchema).describe('스냅 목록 (MUSINSA SNAP2 전용)'),
  })
  .describe('CurationSchema');

/**
 * 내 큐레이션 목록 조회 응답 스키마 (페이징 데이터)
 */
export const GetMyCurationsResponseSchema = createApiResponseSchema(
  z.object({
    list: z.array(CurationSchema).describe('큐레이션 데이터 리스트'),
  }),
);

/**
 * TypeScript 타입 정의
 */
export type GetMyCurationsResponse = z.infer<typeof GetMyCurationsResponseSchema>;

const GetMyCurationsRequestParamsSchema = z.object({
  page: z.number().int().describe('페이지 번호'),
  size: z.number().int().describe('페이지 크기'),
  curationId: z.string().optional().describe('큐레이션 ID'),
  type: z.enum(['GOODS', 'SNAP2']).optional().describe('큐레이션 타입 (MUSINSA)'),
});

type GetMyCurationsRequestParams = z.infer<typeof GetMyCurationsRequestParamsSchema>;

export const getCurationsMe = withApiErrorCapture(
  lazyPipe(
    validateApiRequest(GetMyCurationsRequestParamsSchema),
    async (params: GetMyCurationsRequestParams): Promise<GetMyCurationsResponse> =>
      executeStandardRequest(async () =>
        privateHttpClient.get<GetMyCurationsResponse>(
          urlJoin(curationsApiPath.getCurationsMe),
          params,
        ),
      ),
    validateApiResponse(GetMyCurationsResponseSchema),
  ),
);
